import {
  ownerChatAttachPreviewEl,
  ownerChatAttachThumbEl,
  ownerChatAttachThumbWrapEl,
  ownerChatAttachThumbSpinnerEl,
  ownerChatAttachThumbRingFillEl,
  ownerChatAttachThumbPctEl,
  ownerChatPhotoInput,
  ownerChatSendBtn
} from './dom.js';
import { getOwnerChatPendingPhoto, getOwnerChatIsSending, setOwnerChatPendingPhoto } from './session.js';

const THUMB_RING_R = 15.5;
const THUMB_RING_C = 2 * Math.PI * THUMB_RING_R;

let ownerChatPendingReadGen = 0;

function ownerChatSetPreviewProgress(pct: number) {
  const p = Math.max(0, Math.min(100, Math.round(pct)));
  if (ownerChatAttachThumbPctEl) ownerChatAttachThumbPctEl.textContent = p + '%';
  if (ownerChatAttachThumbRingFillEl) {
    ownerChatAttachThumbRingFillEl.style.strokeDasharray = String(THUMB_RING_C);
    ownerChatAttachThumbRingFillEl.style.strokeDashoffset = String(THUMB_RING_C * (1 - p / 100));
  }
  const loading = p < 100;
  if (ownerChatAttachThumbWrapEl) {
    ownerChatAttachThumbWrapEl.classList.toggle('chat-attach-thumb-wrap--loading', loading);
  }
  if (ownerChatAttachThumbSpinnerEl) {
    ownerChatAttachThumbSpinnerEl.setAttribute('aria-hidden', loading ? 'false' : 'true');
  }
}

function ownerChatResetPreviewProgress() {
  ownerChatSetPreviewProgress(0);
}

export function isOwnerChatPendingPhotoReady() {
  const pending = getOwnerChatPendingPhoto();
  if (!pending) return true;
  return !!pending.previewReady && !!pending.packed;
}

export function ownerChatSyncSendButtonState() {
  if (!ownerChatSendBtn) return;
  const waitingPhoto = !!getOwnerChatPendingPhoto() && !isOwnerChatPendingPhotoReady();
  const disabled = getOwnerChatIsSending() || waitingPhoto;
  ownerChatSendBtn.disabled = disabled;
  ownerChatSendBtn.classList.toggle('chat-send-btn--waiting', !!waitingPhoto);
  ownerChatSendBtn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
}

function ownerChatHideAttachPreview(immediate?: boolean) {
  if (!ownerChatAttachPreviewEl) return;
  const el = ownerChatAttachPreviewEl;
  el.classList.remove('chat-attach-preview--open');
  el.setAttribute('aria-hidden', 'true');
  if (immediate) {
    el.hidden = true;
    return;
  }
  const finish = function () {
    if (!el.classList.contains('chat-attach-preview--open')) el.hidden = true;
  };
  let done = false;
  const onEnd = function (ev: TransitionEvent) {
    if (ev.target !== el) return;
    if (done) return;
    done = true;
    el.removeEventListener('transitionend', onEnd);
    finish();
  };
  el.addEventListener('transitionend', onEnd);
  setTimeout(finish, 380);
}

function ownerChatShowAttachPreview() {
  if (!ownerChatAttachPreviewEl) return;
  const el = ownerChatAttachPreviewEl;
  el.hidden = false;
  el.setAttribute('aria-hidden', 'false');
  el.classList.remove('chat-attach-preview--open');
  requestAnimationFrame(function () {
    requestAnimationFrame(function () {
      el.classList.add('chat-attach-preview--open');
    });
  });
}

export function ownerChatClearPendingPhoto(immediate?: boolean, revokeObjectUrl = true) {
  ownerChatPendingReadGen += 1;
  var pending = getOwnerChatPendingPhoto();
  if (revokeObjectUrl && pending && pending.objectUrl) {
    try { URL.revokeObjectURL(pending.objectUrl); } catch (_) {}
  }
  setOwnerChatPendingPhoto(null);
  ownerChatResetPreviewProgress();
  if (ownerChatAttachThumbEl) ownerChatAttachThumbEl.removeAttribute('src');
  ownerChatHideAttachPreview(!!immediate);
  if (ownerChatPhotoInput) {
    try { ownerChatPhotoInput.value = ''; } catch (_) {}
  }
  ownerChatSyncSendButtonState();
}

async function ownerChatPreloadPendingPhoto(file: File, objectUrl: string, gen: number) {
  try {
    const packed = await ownerChatReadPhotoBase64(file, function (pct) {
      if (gen !== ownerChatPendingReadGen) return;
      ownerChatSetPreviewProgress(pct);
    });
    if (gen !== ownerChatPendingReadGen) return;
    const pending = getOwnerChatPendingPhoto();
    if (!pending || pending.objectUrl !== objectUrl) return;
    setOwnerChatPendingPhoto({
      file: pending.file,
      objectUrl: pending.objectUrl,
      mime: packed.mime,
      previewReady: true,
      packed
    });
    ownerChatSetPreviewProgress(100);
    ownerChatSyncSendButtonState();
  } catch (_) {
    if (gen !== ownerChatPendingReadGen) return;
    const pending = getOwnerChatPendingPhoto();
    if (!pending || pending.objectUrl !== objectUrl) return;
    setOwnerChatPendingPhoto({
      file: pending.file,
      objectUrl: pending.objectUrl,
      mime: pending.mime,
      previewReady: false,
      packed: null
    });
    ownerChatSetPreviewProgress(0);
    if (ownerChatAttachThumbPctEl) ownerChatAttachThumbPctEl.textContent = '!';
    ownerChatSyncSendButtonState();
  }
}

export function ownerChatSetPendingPhoto(file: File) {
  if (!file) return;
  ownerChatPendingReadGen += 1;
  const readGen = ownerChatPendingReadGen;

  var pending = getOwnerChatPendingPhoto();
  if (pending) {
    if (pending.objectUrl) {
      try { URL.revokeObjectURL(pending.objectUrl); } catch (_) {}
    }
  } else {
    ownerChatHideAttachPreview(true);
  }

  const objectUrl = URL.createObjectURL(file);
  setOwnerChatPendingPhoto({
    file: file,
    objectUrl: objectUrl,
    mime: file.type || 'image/jpeg',
    previewReady: false,
    packed: null
  });
  ownerChatSetPreviewProgress(0);
  ownerChatSyncSendButtonState();
  ownerChatShowAttachPreview();

  if (ownerChatAttachThumbEl) {
    ownerChatAttachThumbEl.src = objectUrl;
  }

  void ownerChatPreloadPendingPhoto(file, objectUrl, readGen);
}

/** Read the picked file as-is (no resize/recompress). */
export async function ownerChatReadPhotoBase64(
  file: File,
  onProgress?: (pct: number) => void
): Promise<{
  mime: string;
  base64: string;
  width: number | null;
  height: number | null;
}> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result || ''));
    fr.onerror = () => reject(new Error('read_failed'));
    fr.onprogress = function (ev) {
      if (!onProgress || !ev.lengthComputable || ev.total <= 0) return;
      onProgress(Math.round((ev.loaded / ev.total) * 100));
    };
    fr.readAsDataURL(file);
  });
  const m = /^data:([^;]+);base64,(.+)$/.exec(dataUrl);
  if (!m || !m[2]) throw new Error('bad_image_read');
  const fromDataUrl = String(m[1] || '').trim().toLowerCase();
  const fromFile = String(file.type || '').trim().toLowerCase();
  const mime = /^image\//i.test(fromDataUrl)
    ? fromDataUrl
    : /^image\//i.test(fromFile)
      ? fromFile
      : 'image/jpeg';
  return { mime, base64: m[2], width: null, height: null };
}
